'use client';

import { useState } from 'react';

import { useRouter } from '@/hooks/useRouter';
import { useToastError } from '@/hooks/useToastError';
import { InquiryTypes } from '@/lib/inquiries/inquiry-types.enum';
import { HttpMethods, tryFetchToJson } from '@/utils/http-request';
import { stringifyUrl } from '@/utils/stringify-url';

import { BasicInquiry, ContactOptions } from '../types';
import { BasicInquiryForm } from './BasicInquiryForm';

type DefaultFormProps = {
  option:
    | ContactOptions.General
    | ContactOptions.Marketing
    | ContactOptions.PrAndMedia
    | ContactOptions.Partnerships;
};

export function DefaultForm({ option }: DefaultFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const { showToast, hideToast } = useToastError();

  const goToThankYouPage = ({ firstName, email }: BasicInquiry) => {
    const url = stringifyUrl('/contact-us/thank-you', {
      email,
      firstName,
    });

    router.push(url);
  };

  const handleSubmit = async (inquiry: BasicInquiry) => {
    setIsLoading(true);
    hideToast();
    await tryFetchToJson<null>({
      fetchParams: {
        input: '/api/inquiries',
        init: {
          method: HttpMethods.Post,
          body: JSON.stringify({
            ...inquiry,
            type: InquiryTypeMap.get(option),
          }),
          headers: {
            'Content-Type': 'application/json',
          },
        },
      },
      onSuccess: () => goToThankYouPage(inquiry),
      onFunctionalError: () => {
        setIsLoading(false);
        showToast();
      },
      onTechnicalError: () => {
        setIsLoading(false);
        showToast();
      },
    });
  };

  return <BasicInquiryForm isLoading={isLoading} onSubmit={handleSubmit} />;
}

const InquiryTypeMap = new Map<ContactOptions, InquiryTypes>([
  [ContactOptions.General, InquiryTypes.General],
  [ContactOptions.Marketing, InquiryTypes.Marketing],
  [ContactOptions.PrAndMedia, InquiryTypes.PR],
  [ContactOptions.Partnerships, InquiryTypes.Partnerships],
]);
